import { useEffect, useMemo, useRef, useState } from "react";
import type { ActivityHistoryItem, ProductCreationRequest } from "./types";
import {
  groupActivitiesByVersion,
  hasEmptyStates,
  segmentActivitiesByGroupable,
} from "./utils";
import { ErrorPlaceholder } from "../../../../components/Error";
import { useTranslation } from "react-i18next";
import { AccordionCardWithStatus } from "../../../../components/AccordionCard/AccordionCardWithStatus";
import { Activity } from "./Activity";
import { ActivityBulkUploadItem } from "./BulkUploadItem";
import { Container, DataState, LoadingIconContainer } from "./styles";
import { LoadingIcon } from "../../../../components/Icons/Icons";
import { parseCollections } from "./collections";
import { CollectionItem } from "./CollectionItem";
import { parseGroups } from "./groups";
import { GroupItem } from "./GroupItem";
import { Assets } from "../../../../types/types.PIM";
import type { ActivityTargetType } from "../../../../util/activityEndpoints";
import { useStoreState } from "../../../../util/util";
import { useMdmEntityDisplayNames } from "../../../../util/mdmEntityDisplayNames";
import { useActivities } from "./useActivities";
import { APIPushItem } from "./APIPushItem";
import { spacingTokens } from "../../../../designTokens";

interface ActivityHistoryListProps {
  data: ActivityHistoryItem[];
  isLoading?: boolean;
  error?: string;
  targetId?: string;
  targetType?: ActivityTargetType;
  isTabActive?: boolean;
  loadMore?: {
    isLoading: boolean;
    onLoad: () => void;
  };
}

// load more data on scrolling till reached to the total
function PaginationScrollTrigger({
  enabled,
  onIntersect,
}: {
  enabled: boolean;
  onIntersect: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const onIntersectRef = useRef(onIntersect);
  onIntersectRef.current = onIntersect;

  useEffect(() => {
    if (!enabled || !ref.current) return;
    const el = ref.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onIntersectRef.current();
        }
      },
      { rootMargin: `120px ${spacingTokens.spacing_none}` }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled]);

  return <div ref={ref} style={{ height: 1 }} />;
}

function mergeBulkAssetAssociations(
  items: ActivityHistoryItem[]
): ActivityHistoryItem[] {
  if (!items || items.length === 0) {
    return items;
  }

  const consumedIndices = new Set<number>();
  const reverseResult: ActivityHistoryItem[] = [];

  for (let i = items.length - 1; i >= 0; i--) {
    if (consumedIndices.has(i)) {
      continue;
    }

    const item = items[i];
    const assetIds = item.activity_args?.asset_ids;

    if (item.activity_args?.job_number && assetIds && assetIds.length > 0) {
      const assetIdSet = new Set<string>(assetIds);
      const collectedAssets: Assets[] = [];
      if (item.state_after.assets && item.state_after.assets.length > 0) {
        collectedAssets.push(...item.state_after.assets);
        consumedIndices.add(i);
      } else {
        for (let j = i - 1; j >= 0 && assetIdSet.size > 0; j--) {
          if (consumedIndices.has(j)) {
            continue;
          }

          const candidate = items[j];
          const candidateAsset =
            candidate.state_after?.asset || candidate.state_before?.asset;

          if (
            candidateAsset &&
            candidateAsset.id &&
            assetIdSet.has(candidateAsset.id)
          ) {
            collectedAssets.push(candidateAsset as Assets);
            consumedIndices.add(j);
            assetIdSet.delete(candidateAsset.id);
            continue;
          }
        }
      }

      if (collectedAssets.length > 0) {
        const mergedItem: ActivityHistoryItem = {
          ...item,
          target_type: "bulk-association",
          state_after: {
            ...(item.state_after || {}),
            assets: [...(item.state_after?.assets ?? []), ...collectedAssets],
          },
        };
        reverseResult.push(mergedItem);
        continue;
      }
    }

    reverseResult.push(item);
  }

  return reverseResult.reverse();
}

function mergeProductCreationRequestGroups(
  items: ActivityHistoryItem[]
): ActivityHistoryItem[] {
  if (!items || items.length === 0) {
    return items;
  }

  const nonGroupedItems: ActivityHistoryItem[] = [];
  for (const item of items) {
    const productCreationRequest = item.activity_args
      ?.product_creation_request as ProductCreationRequest | undefined;
    const isProductCreationRequest =
      Boolean(productCreationRequest) &&
      Boolean(
        item.state_after.status === "draft" &&
          item.state_before.status === "creation_requested"
      );

    // skip all the items after product_creation_request as they are form items only
    if (isProductCreationRequest) {
      nonGroupedItems.push(item);
      break;
    }

    nonGroupedItems.push(item);
  }

  return nonGroupedItems;
}

export function ActivityHistoryList({
  data,
  isLoading = false,
  error,
  targetId,
  targetType,
  isTabActive = true,
  loadMore,
}: ActivityHistoryListProps) {
  const { t } = useTranslation();
  const { getLabel } = useMdmEntityDisplayNames();
  const { tenant_id } = useStoreState();
  const tabPrev = useRef({ active: false, targetId: "", tenant_id: "" });
  // currently expanded version
  const [expandedVersions, setExpandedVersions] = useState<Set<number>>(
    new Set()
  );
  // Version number currently being fetched from API
  const [detailApiVersion, setDetailApiVersion] = useState<number | null>(null);
  // "session" id for Activity tab detail state.
  const [accordionEpoch, setAccordionEpoch] = useState(0);
  // Cached detail items per version number.
  const [detailedByVersion, setDetailedByVersion] = useState<
    Record<number, ActivityHistoryItem[]>
  >({});
  const [hasMoreByVersion, setHasMoreByVersion] = useState<
    Record<number, boolean>
  >({});
  // Marks which accordionEpoch last populated a version's details.
  const [fetchedEpochByVersion, setFetchedEpochByVersion] = useState<
    Record<number, number>
  >({});

  const detailFetchEpochRef = useRef<number>(0);
  const detailFetchVersionRef = useRef<number | null>(null);
  const { currentItems, previousItems } = useMemo(() => {
    let mergedData = mergeProductCreationRequestGroups(data);
    mergedData = mergeBulkAssetAssociations(mergedData);

    const prev: ActivityHistoryItem[] = []; //Previous History Items
    const curr: ActivityHistoryItem[] = []; //Current History Items

    (mergedData || []).forEach((item) => {
      const isLegacyHistory =
        item.legacy_history === true ||
        (item.legacy_history === undefined && hasEmptyStates(item));
      if (isLegacyHistory) {
        prev.push(item);
        return;
      }

      const passesFilters =
        !item.state_after?.teams &&
        !item.state_before?.teams &&
        !item.state_after?.subscribers &&
        !item.state_before?.subscribers &&
        !item.state_after?.visibility_tenants &&
        !item.state_before?.visibility_tenants &&
        !item.state_after?.is_accessible_by_all_customers_and_distributors &&
        !item.state_before?.is_accessible_by_all_customers_and_distributors &&
        !item.state_after?.visibility_storefront &&
        !item.state_before?.visibility_storefront;

      if (passesFilters) {
        curr.push(item);
      }
    });

    return { currentItems: curr, previousItems: prev };
  }, [data]);

  const versionGroups = useMemo(
    () => groupActivitiesByVersion(currentItems),
    [currentItems]
  );

  const {
    items: detailItems,
    pages: detailPages,
    error: detailsError,
    isValidating: isDetailPagesValidating,
    hasMore: hasMoreDetailPages,
    loadMore: loadMoreDetailPages,
  } = useActivities({
    tenantId: tenant_id,
    targetId,
    targetType,
    enabled: isTabActive && detailApiVersion !== null,
    includeDetails: true,
    versionNumber: detailApiVersion,
  });

  useEffect(() => {
    if (!targetId || !tenant_id) return;

    const prev = tabPrev.current;
    tabPrev.current = {
      active: isTabActive,
      targetId,
      tenant_id,
    };

    const shouldReset =
      targetId !== prev.targetId ||
      tenant_id !== prev.tenant_id ||
      (isTabActive && !prev.active) ||
      (prev.targetId === "" && prev.tenant_id === "" && isTabActive);

    if (!shouldReset) {
      return;
    }

    setExpandedVersions(new Set());
    setDetailApiVersion(null);
    setDetailedByVersion({});
    setHasMoreByVersion({});
    setFetchedEpochByVersion({});
    setAccordionEpoch((n) => n + 1);
  }, [isTabActive, targetId, tenant_id]);

  useEffect(() => {
    if (detailPages.length === 0 || detailApiVersion === null) return;
    if (detailFetchEpochRef.current !== accordionEpoch) return;
    if (detailFetchVersionRef.current !== detailApiVersion) return;

    if (detailItems.length === 0) {
      // reset values when no items are found
      setHasMoreByVersion((prev) => {
        const next = { ...prev };
        delete next[detailApiVersion];
        return next;
      });
      setExpandedVersions((prev) => {
        const next = new Set(prev);
        next.delete(detailApiVersion);
        return next;
      });
      setDetailApiVersion(null);
      setAccordionEpoch((prev) => prev + 1);
      return;
    }
    setHasMoreByVersion((prev) => ({
      ...prev,
      [detailApiVersion]: hasMoreDetailPages,
    }));
    setDetailedByVersion((prev) => ({
      ...prev,
      [detailApiVersion]: detailItems,
    }));
    setFetchedEpochByVersion((prev) => ({
      ...prev,
      [detailApiVersion]: accordionEpoch,
    }));
  }, [
    detailItems,
    detailPages.length,
    detailApiVersion,
    hasMoreDetailPages,
    accordionEpoch,
  ]);

  const handleVersionToggle = (version: number, expanded: boolean) => {
    setExpandedVersions((prev) => {
      const next = new Set(prev);
      if (expanded) {
        next.add(version);
      } else {
        next.delete(version);
      }
      return next;
    });

    if (!expanded) {
      setDetailApiVersion((prev) => (prev === version ? null : prev));
      return;
    }

    const hasRows = (detailedByVersion[version]?.length ?? 0) > 0;
    const fullyLoaded = hasMoreByVersion[version] === false && hasRows;
    const fetchedThisSession =
      fetchedEpochByVersion[version] === accordionEpoch;
    if (fullyLoaded && fetchedThisSession) {
      return;
    }
    detailFetchEpochRef.current = accordionEpoch;
    detailFetchVersionRef.current = version;
    setDetailApiVersion(version);
  };

  const renderVersionItems = (items: ActivityHistoryItem[]) => {
    const segs = segmentActivitiesByGroupable(items);
    const renderedItems = segs
      .flatMap((seg, idx) => {
        // render bulk activity
        if (seg.key?.includes("bulk") && seg.items.length > 0) {
          return seg.items.map((item) => {
            return (
              <ActivityBulkUploadItem
                key={`bulk-${idx}-${item.id}`}
                item={item}
              />
            );
          });
        }

        // render collections
        if (seg.groupable) {
          const results = parseCollections(seg.items, t);
          if (results.length > 0) {
            return (
              <CollectionItem
                key={`grouped-${idx}-${results[0].id}`}
                results={results}
              />
            );
          }
          return null;
        }

        // render barentz api push
        const isApiPush = (item: ActivityHistoryItem) =>
          item.activity_args?.kind === "barentz_master_data_api_push";
        const hasApiPush = seg.items.some(isApiPush);
        const allApiPush = hasApiPush && seg.items.every(isApiPush);

        if (hasApiPush) {
          if (allApiPush) {
            return seg.items.map((item) => (
              <APIPushItem key={`api-push-${idx}-${item.id}`} item={item} />
            ));
          }
          return seg.items.flatMap((item) => {
            if (isApiPush(item)) {
              return [
                <APIPushItem key={`api-push-${idx}-${item.id}`} item={item} />,
              ];
            }
            const groupResults = parseGroups([item], t, targetType, getLabel);
            return groupResults.map((result) => (
              <GroupItem key={result.id} result={result} />
            ));
          });
        }

        // render groups
        const groupResults = parseGroups(seg.items, t, targetType, getLabel);
        if (groupResults.length > 0) {
          return groupResults.map((result) => (
            <GroupItem key={result.id} result={result} />
          ));
        }
        return null;
      })
      .filter(Boolean);

    if (renderedItems.length === 0) {
      return <DataState>{t("No history found for this version")}</DataState>;
    }

    return <div style={{ position: "relative" }}>{renderedItems}</div>;
  };

  if (isLoading) {
    return (
      <LoadingIconContainer>
        <LoadingIcon width={24} height={24} />
      </LoadingIconContainer>
    );
  }

  if (error) {
    return <ErrorPlaceholder message={t("Error loading activity history")} />;
  }

  if (
    (!currentItems || currentItems.length === 0) &&
    (!previousItems || previousItems.length === 0)
  ) {
    return <DataState>{t("No activity history found")}</DataState>;
  }

  return (
    <Container>
      {versionGroups.length > 0 &&
        versionGroups.map((versionGroup) => (
          <AccordionCardWithStatus
            key={`version-${versionGroup.version_number}-${accordionEpoch}`}
            title={`Version ${versionGroup.version_number}`}
            status={versionGroup.status}
            onToggle={(expanded) =>
              handleVersionToggle(versionGroup.version_number, expanded)
            }
          >
            {(() => {
              const version = versionGroup.version_number;
              const isExpanded = expandedVersions.has(version);
              const versionDetails = detailedByVersion[version];

              if (!isExpanded) {
                return null;
              }

              if (
                !versionDetails &&
                !detailsError &&
                detailApiVersion === version
              ) {
                return (
                  <LoadingIconContainer>
                    <LoadingIcon width={24} height={24} />
                  </LoadingIconContainer>
                );
              }

              if (
                detailsError &&
                !versionDetails &&
                detailApiVersion === version
              ) {
                return (
                  <ErrorPlaceholder
                    message={t("Error loading activity history details")}
                  />
                );
              }

              return (
                <>
                  {renderVersionItems(versionDetails || [])}
                  {detailApiVersion === version && hasMoreDetailPages && (
                    <>
                      <PaginationScrollTrigger
                        enabled={!isDetailPagesValidating}
                        onIntersect={loadMoreDetailPages}
                      />
                      <LoadingIconContainer>
                        <LoadingIcon width={20} height={20} />
                      </LoadingIconContainer>
                    </>
                  )}
                </>
              );
            })()}
          </AccordionCardWithStatus>
        ))}

      {previousItems.length > 0 && targetId && targetType && (
        <AccordionCardWithStatus
          title={t("Previous history")}
          key={`previous-history-${previousItems.length}`}
        >
          <Activity
            target_id={targetId}
            mode={targetType}
            activityData={previousItems}
          />
        </AccordionCardWithStatus>
      )}

      {loadMore && (
        <>
          <PaginationScrollTrigger
            enabled={!loadMore.isLoading}
            onIntersect={loadMore.onLoad}
          />
          {loadMore.isLoading && (
            <LoadingIconContainer>
              <LoadingIcon width={24} height={24} />
            </LoadingIconContainer>
          )}
        </>
      )}
    </Container>
  );
}
